import { useCallback, useState } from 'react';

import { useStore } from '../motionEditorStore';
import { END_EFFECTOR_COLORS, END_EFFECTOR_NAMES } from '../types';

export default function ControlPanel() {
  const store = useStore();
  const [motionInput, setMotionInput] = useState(store.state.motionID);

  const handleLoad = useCallback(() => {
    store.updateMotionID(motionInput);
    store.loadMotion();
  }, [motionInput, store]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleLoad();
    },
    [handleLoad],
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Motion ID Input */}
      <div className="border-primary bg-secondary rounded-lg border p-3">
        <h3 className="text-primary mb-2 text-sm font-semibold">Load Motion</h3>

        <div className="flex flex-col gap-2">
          <input
            type="text"
            value={motionInput}
            onChange={(e) => setMotionInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter motion ID (e.g. mogen-job-...)"
            className="border-primary bg-primary text-primary placeholder-gray-45 focus:border-blue w-full rounded border px-3 py-2 text-sm focus:outline-none"
          />

          <details className="text-secondary text-xs">
            <summary className="hover:text-primary cursor-pointer">
              API Key
            </summary>
            <input
              type="text"
              value={store.state.apiKey}
              onChange={(e) => store.updateApiKey(e.target.value)}
              className="border-primary bg-primary text-primary focus:border-blue mt-1 w-full rounded border px-2 py-1 font-mono text-xs focus:outline-none"
            />
          </details>

          <button
            onClick={handleLoad}
            disabled={store.state.loading || !motionInput.trim()}
            className="bg-blue hover:bg-blue-60 rounded px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {store.state.loading ? 'Loading…' : 'Load Motion'}
          </button>
        </div>
      </div>

      {/* End Effector Selection */}
      {store.state.bvhText && (
        <div className="border-primary bg-secondary rounded-lg border p-3">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-primary text-sm font-semibold">
              End Effectors
            </h3>
            <button
              onClick={() => store.toggleTrajectories()}
              className={`rounded px-2 py-0.5 text-[10px] font-medium transition-colors ${
                store.state.showTrajectories
                  ? 'bg-blue text-white'
                  : 'text-secondary hover:text-primary bg-white/10'
              }`}
            >
              {store.state.showTrajectories ? 'Curves ON' : 'Curves'}
            </button>
          </div>
          <p className="text-secondary mb-2 text-xs">
            Click an end effector in the viewer or select below to start
            editing.
          </p>
          <div className="flex flex-wrap gap-1">
            {END_EFFECTOR_NAMES.map((name) => (
              <button
                key={name}
                onClick={() =>
                  store.selectEffector(
                    store.state.selectedEffector === name ? null : name,
                  )
                }
                className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                  store.state.selectedEffector === name
                    ? 'text-white ring-2 ring-current'
                    : 'text-secondary hover:text-primary'
                }`}
                style={{
                  backgroundColor:
                    store.state.selectedEffector === name
                      ? END_EFFECTOR_COLORS[name]
                      : `${END_EFFECTOR_COLORS[name]}40`,
                }}
              >
                {name.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Regenerate & Freeze */}
      {store.state.bvhText && (
        <div className="border-primary bg-secondary rounded-lg border p-3">
          <h3 className="text-primary mb-2 text-sm font-semibold">Generate</h3>
          <button
            onClick={() => store.regenerate()}
            disabled={
              store.state.generating || store.state.constraints.length === 0
            }
            className="hover:bg-green-60 w-full rounded bg-green-50 px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {store.state.generating ? (
              <span className="flex items-center justify-center gap-2">
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Regenerating…
              </span>
            ) : (
              `Regenerate (${store.state.constraints.length} constraint${store.state.constraints.length !== 1 ? 's' : ''})`
            )}
          </button>

          {store.state.editRound > 0 && (
            <p className="text-secondary mt-2 text-xs">
              Edit round {store.state.editRound} — add more constraints, undo, or
              freeze.
            </p>
          )}

          <div className="mt-2 flex gap-2">
            <button
              onClick={() => store.undo()}
              disabled={store.state.editRound === 0}
              className="border-primary bg-primary text-primary hover:bg-secondary flex-1 rounded border px-4 py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              Undo
            </button>

            <button
              onClick={() => store.freeze()}
              disabled={store.state.freezing || !store.state.editBvhURL}
              className="bg-blue hover:bg-blue-60 flex-1 rounded px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              {store.state.freezing ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Freezing…
                </span>
              ) : (
                'Freeze'
              )}
            </button>
          </div>

          {store.state.frozenMotionID && (
            <p className="bg-green-10 text-green-60 mt-2 rounded p-2 font-mono text-xs break-all">
              Saved as: {store.state.frozenMotionID}
            </p>
          )}
        </div>
      )}

      {/* Warning Display */}
      {store.state.warning && (
        <div className="rounded-lg border border-yellow-400/30 bg-yellow-400/10 p-3 text-xs text-yellow-600 dark:text-yellow-400">
          {store.state.warning}
        </div>
      )}

      {/* Error Display */}
      {store.state.error && (
        <div className="border-red-20 bg-red-10 text-red rounded-lg border p-3 text-xs">
          {store.state.error}
        </div>
      )}
    </div>
  );
}
